import {
  createArchitectureEdge,
  createArchitectureNode,
  generateArchitectureJson,
  parseArchitectureJson,
  type ArchitectureDiagram,
  type ArchitectureEdge,
  type ArchitectureNode,
  type ArchitectureProject,
  type EdgeDirection,
  type JsonParseError,
  type NodeCategory,
  type NodeMode,
  type NodePosition,
} from '@hyphen-db-studio/architecture-core';
import { computeNestedAutoLayout } from '@hyphen-db-studio/diagram-engine';
import { create } from 'zustand';
import { useArchitectureProjectsStore } from './useArchitectureProjectsStore';

export const SERVICE_NODE_WIDTH = 120;
export const SERVICE_NODE_HEIGHT = 96;

const AUTO_LAYOUT_DIRECTION: 'LR' | 'TB' = 'LR';

// The handle a node's connection point should reset to so it renders cleanly along the
// auto layout's flow direction, keyed by direction rather than compared with `===` so
// this stays correct if AUTO_LAYOUT_DIRECTION (or a future direction toggle) changes.
const HANDLE_BY_DIRECTION: Record<'LR' | 'TB', { from: string; to: string }> = {
  LR: { from: 'right', to: 'left' },
  TB: { from: 'bottom', to: 'top' },
};

export type ArchitectureSelectionRef = { type: 'node'; id: string } | { type: 'edge'; id: string } | null;

interface HistoryEntry {
  diagram: ArchitectureDiagram;
}

function cloneDiagram(diagram: ArchitectureDiagram): ArchitectureDiagram {
  return JSON.parse(JSON.stringify(diagram));
}

const HISTORY_LIMIT = 100;

interface ArchitectureState {
  projectId: string | null;
  projectName: string;
  documentation: string;
  diagram: ArchitectureDiagram;
  jsonSource: string;
  parseErrors: JsonParseError[];
  selection: ArchitectureSelectionRef;
  dirty: boolean;
  past: HistoryEntry[];
  future: HistoryEntry[];

  loadProject: (project: ArchitectureProject) => void;
  commitDiagram: (next: ArchitectureDiagram, opts?: { snapshot?: boolean }) => void;
  setJsonSource: (text: string) => void;
  undo: () => void;
  redo: () => void;
  setSelection: (sel: ArchitectureSelectionRef) => void;
  rename: (name: string) => void;
  setDocumentation: (doc: string) => void;

  addNode: (kind: string, category: NodeCategory, label: string, mode?: NodeMode) => ArchitectureNode;
  updateNode: (id: string, patch: Partial<Pick<ArchitectureNode, 'label' | 'note' | 'color' | 'kind' | 'category'>>) => void;
  deleteNode: (id: string) => void;
  deleteNodes: (ids: string[]) => void;
  setNodePosition: (id: string, position: NodePosition, snapshot?: boolean) => void;
  setNodePositions: (positions: Record<string, NodePosition>, snapshot?: boolean) => void;
  setNodeMode: (id: string, mode: NodeMode) => void;
  setNodeParent: (id: string, parentId: string | null) => void;

  addEdge: (fromNodeId: string, toNodeId: string, fromHandle?: string | null, toHandle?: string | null, direction?: EdgeDirection) => void;
  updateEdge: (id: string, patch: Partial<Pick<ArchitectureEdge, 'label' | 'direction' | 'animated' | 'speed'>>) => void;
  deleteEdge: (id: string) => void;

  autoLayout: () => void;
  save: () => void;
}

function emptyState() {
  const diagram: ArchitectureDiagram = { nodes: [], edges: [] };
  return {
    projectId: null as string | null,
    projectName: 'Untitled',
    documentation: '',
    diagram,
    jsonSource: generateArchitectureJson(diagram),
    parseErrors: [] as JsonParseError[],
    selection: null as ArchitectureSelectionRef,
    dirty: false,
    past: [] as HistoryEntry[],
    future: [] as HistoryEntry[],
  };
}

export const useArchitectureStore = create<ArchitectureState>((set, get) => ({
  ...emptyState(),

  loadProject: (project) => {
    set({
      projectId: project.id,
      projectName: project.name,
      documentation: project.documentation ?? '',
      diagram: project.diagram,
      jsonSource: generateArchitectureJson(project.diagram),
      parseErrors: [],
      selection: null,
      dirty: false,
      past: [],
      future: [],
    });
  },

  commitDiagram: (next, opts = {}) => {
    const { snapshot = true } = opts;
    const prev = { diagram: get().diagram };
    set((state) => ({
      diagram: next,
      jsonSource: generateArchitectureJson(next),
      parseErrors: [],
      dirty: true,
      past: snapshot ? [...state.past, prev].slice(-HISTORY_LIMIT) : state.past,
      future: snapshot ? [] : state.future,
    }));
  },

  // Unlike DBML's forgiving parser, invalid JSON can't be partially recovered — on a
  // parse/validation error we keep the last-good diagram and just surface the errors,
  // so the diagram/canvas never ends up in a broken state from an in-progress edit.
  setJsonSource: (text) => {
    const { diagram, errors } = parseArchitectureJson(text);
    if (errors.length > 0 || !diagram) {
      set({ jsonSource: text, parseErrors: errors });
      return;
    }
    const prev = { diagram: get().diagram };
    set((state) => ({
      jsonSource: text,
      diagram,
      parseErrors: [],
      dirty: true,
      past: [...state.past, prev].slice(-HISTORY_LIMIT),
      future: [],
    }));
  },

  undo: () => {
    const { past } = get();
    if (!past.length) return;
    const previous = past[past.length - 1];
    const current = { diagram: get().diagram };
    set({
      diagram: previous.diagram,
      jsonSource: generateArchitectureJson(previous.diagram),
      parseErrors: [],
      past: past.slice(0, -1),
      future: [current, ...get().future].slice(0, HISTORY_LIMIT),
      dirty: true,
    });
  },

  redo: () => {
    const { future } = get();
    if (!future.length) return;
    const next = future[0];
    const current = { diagram: get().diagram };
    set({
      diagram: next.diagram,
      jsonSource: generateArchitectureJson(next.diagram),
      parseErrors: [],
      future: future.slice(1),
      past: [...get().past, current].slice(-HISTORY_LIMIT),
      dirty: true,
    });
  },

  setSelection: (sel) => set({ selection: sel }),

  rename: (name) => set({ projectName: name, dirty: true }),
  setDocumentation: (doc) => set({ documentation: doc, dirty: true }),

  addNode: (kind, category, label, mode) => {
    const diagram = get().diagram;
    const node = createArchitectureNode(kind, category, label, mode);
    const next = { ...diagram, nodes: [...diagram.nodes, node] };
    get().commitDiagram(next);
    get().setSelection({ type: 'node', id: node.id });
    return node;
  },

  updateNode: (id, patch) => {
    const diagram = get().diagram;
    const next = { ...diagram, nodes: diagram.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)) };
    get().commitDiagram(next);
  },

  deleteNode: (id) => {
    const diagram = get().diagram;
    const next: ArchitectureDiagram = {
      // Deleting a group-mode node ungroups its members instead of deleting them too.
      nodes: diagram.nodes
        .filter((n) => n.id !== id)
        .map((n) => (n.parentId === id ? { ...n, parentId: null } : n)),
      edges: diagram.edges.filter((e) => e.fromNodeId !== id && e.toNodeId !== id),
    };
    get().commitDiagram(next);
    if (get().selection?.type === 'node' && get().selection?.id === id) get().setSelection(null);
  },

  deleteNodes: (ids) => {
    const drop = new Set(ids);
    const diagram = get().diagram;
    const next: ArchitectureDiagram = {
      // Deleting a group-mode node ungroups its surviving members instead of deleting them too.
      nodes: diagram.nodes
        .filter((n) => !drop.has(n.id))
        .map((n) => (n.parentId && drop.has(n.parentId) ? { ...n, parentId: null } : n)),
      edges: diagram.edges.filter((e) => !drop.has(e.fromNodeId) && !drop.has(e.toNodeId)),
    };
    get().commitDiagram(next);
    const sel = get().selection;
    if (sel?.type === 'node' && drop.has(sel.id)) get().setSelection(null);
  },

  setNodePosition: (id, position, snapshot = false) => {
    const diagram = get().diagram;
    const next = { ...diagram, nodes: diagram.nodes.map((n) => (n.id === id ? { ...n, position } : n)) };
    get().commitDiagram(next, { snapshot });
  },

  setNodePositions: (positions, snapshot = false) => {
    const diagram = get().diagram;
    const next = {
      ...diagram,
      nodes: diagram.nodes.map((n) => (positions[n.id] ? { ...n, position: positions[n.id] } : n)),
    };
    get().commitDiagram(next, { snapshot });
  },

  setNodeMode: (id, mode) => {
    const diagram = get().diagram;
    const next: ArchitectureDiagram = {
      ...diagram,
      nodes: diagram.nodes.map((n) => {
        if (n.id === id) return { ...n, mode, parentId: mode === 'group' ? null : n.parentId };
        // Switching a container back to component mode ungroups its members.
        if (mode === 'component' && n.parentId === id) return { ...n, parentId: null };
        return n;
      }),
    };
    get().commitDiagram(next);
  },

  setNodeParent: (id, parentId) => {
    const diagram = get().diagram;
    if (id === parentId) return;
    const target = diagram.nodes.find((n) => n.id === id);
    const parent = parentId ? diagram.nodes.find((n) => n.id === parentId) : null;
    if (!target) return;
    if (target.mode === 'group') return; // no nested groups
    if (parentId && parent?.mode !== 'group') return;
    const next = { ...diagram, nodes: diagram.nodes.map((n) => (n.id === id ? { ...n, parentId } : n)) };
    get().commitDiagram(next);
  },

  addEdge: (fromNodeId, toNodeId, fromHandle = null, toHandle = null, direction = 'forward') => {
    const diagram = get().diagram;
    const edge = createArchitectureEdge(fromNodeId, toNodeId, direction, fromHandle, toHandle);
    const next = { ...diagram, edges: [...diagram.edges, edge] };
    get().commitDiagram(next);
  },

  updateEdge: (id, patch) => {
    const diagram = get().diagram;
    const next = { ...diagram, edges: diagram.edges.map((e) => (e.id === id ? { ...e, ...patch } : e)) };
    get().commitDiagram(next);
  },

  deleteEdge: (id) => {
    const diagram = get().diagram;
    const next = { ...diagram, edges: diagram.edges.filter((e) => e.id !== id) };
    get().commitDiagram(next);
    if (get().selection?.type === 'edge' && get().selection?.id === id) get().setSelection(null);
  },

  autoLayout: () => {
    const diagram = get().diagram;
    const direction = AUTO_LAYOUT_DIRECTION;
    const positions = computeNestedAutoLayout(
      diagram.nodes.map((n) => ({
        id: n.id,
        width: SERVICE_NODE_WIDTH,
        height: SERVICE_NODE_HEIGHT,
        parentId: n.parentId,
      })),
      diagram.edges.map((e) => ({ from: e.fromNodeId, to: e.toNodeId })),
      { direction },
    );
    // Auto layout arranges every node along `direction`, so every edge's connection
    // point is reset to the side that matches it — otherwise an edge left anchored to
    // whichever handle was manually dragged before the rearrangement would cross back
    // over the freshly laid-out nodes.
    const { from: fromHandle, to: toHandle } = HANDLE_BY_DIRECTION[direction];
    const next: ArchitectureDiagram = {
      nodes: diagram.nodes.map((n) => (positions[n.id] ? { ...n, position: positions[n.id] } : n)),
      edges: diagram.edges.map((e) => ({ ...e, fromHandle, toHandle })),
    };
    get().commitDiagram(next, { snapshot: false });
  },

  save: () => {
    const state = get();
    if (!state.projectId) return;
    const project: ArchitectureProject = {
      id: state.projectId,
      name: state.projectName,
      diagram: cloneDiagram(state.diagram),
      documentation: state.documentation,
      createdAt: useArchitectureProjectsStore.getState().get(state.projectId)?.createdAt ?? new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    useArchitectureProjectsStore.getState().upsert(project);
    set({ dirty: false });
  },
}));
